'use client';
import { useMemo } from 'react';

import Container from '@/components/@base/container';
import Item from '@/components/app/cart/components/list/item';
import Loading from '@/components/app/cart/components/list/item/loading';
import { useGetCart } from '@/components/app/cart/services/hooks';
import { loadingGenerator } from '@/components/shared/helpers';

import { useAddonsStore } from '@/store/addons.store';

import AddonModel from '@/models/addon.model';
import ProductModel from '@/models/product.model';

type GroupedData = {
  newAddons?: AddonModel[];
  addons?: ProductModel[];
  main?: ProductModel;
};
export default function List() {
  const { data, isRefetching, isLoading } = useGetCart();

  const { newSelectedAddons, removedAddons } = useAddonsStore();

  const groupedProducts = useMemo(() => {
    if (!data?.products) return [];

    const result: GroupedData[] = [];
    let currentGroup: GroupedData = {} as GroupedData;

    const computeNewAddons = (group: GroupedData): GroupedData => {
      const originalAddons = ((group.main && group.main.addons) || []) as AddonModel[];
      const selectedTitles =
        group.addons && group.addons.map((a) => (a.addon as AddonModel).title);

      const newAddons = originalAddons.filter((addon) => {
        if (
          newSelectedAddons?.hasOwnProperty(addon.type) &&
          group.main?.product_id === newSelectedAddons[addon.type]?.id
        ) {
          return true;
        }
        if (
          removedAddons?.some(
            (removedAddon) =>
              removedAddon.title === addon.title && removedAddon.id === group.main?.product_id,
          )
        ) {
          return false;
        }
        return !selectedTitles?.includes(addon.title);
      });

      return { ...group, newAddons };
    };

    data.products.forEach((item) => {
      if (!item.addon) {
        if (currentGroup.main && currentGroup.addons) {
          result.push(computeNewAddons(currentGroup));
        }
        currentGroup = { main: item, addons: [] };
      } else {
        currentGroup?.addons?.push(item);
      }
    });

    if (currentGroup.main && currentGroup.addons) {
      result.push(computeNewAddons(currentGroup));
    }

    return result;
  }, [data, newSelectedAddons, removedAddons]);

  return (
    <Container className='mt-[25px] md:mt-0'>
      {isRefetching || isLoading
        ? loadingGenerator(2, () => <Loading />)
        : data?.products?.map((item, index) => {
            const group = groupedProducts.find(
              (pro) => pro?.main?.product_id === item?.product_id,
            );

            return (
              <Item
                addonList={group?.newAddons || []}
                selectedAddonList={group?.addons || []}
                data={item}
                key={item.id}
                index={index}
                isLastAddon={!data?.products?.[index + 1]?.addon}
                productsCount={groupedProducts?.length}
                max={group?.main?.quantity}
                hideFeatures={false}
              />
            );
          })}
    </Container>
  );
}
